import { useState, useCallback } from 'react';
import * as voicifyApi from '../api';
import ApplicationModel from '../models/applications/api/ApplicationModel';
import AppliedApplicationTemplateContainer from '../state/containers/AppliedApplicationTemplateContainer';
import ApplicationEnvironmentsContainer from '../state/containers/ApplicationEnvironmentsContainer';

export interface BulkOperationResult {
    success: boolean;
    appName: string;
    error?: string;
}

export interface UseBulkAppOperationsOptions {
    appliedTemplateContainer?: AppliedApplicationTemplateContainer;
    applicationEnvironmentsContainer?: ApplicationEnvironmentsContainer;
}

export interface UseBulkAppOperationsReturn {
    isProcessing: boolean;
    logs: string[];
    syncSingleModule: (
        appId: string,
        moduleId: string,
        syncName: string,
        denom: number | string,
        numerator: number | string
    ) => Promise<string | null>;
    syncApps: (
        apps: ApplicationModel[],
        sharedTopicsApp?: ApplicationModel | null,
        concurrencyLimit?: number
    ) => Promise<{ successCount: number; errorCount: number }>;
    publishApps: (
        apps: ApplicationModel[],
        concurrencyLimit?: number
    ) => Promise<{ successCount: number; errorCount: number }>;
    clearLogs: () => void;
}

// Handles org level bulk operations for applications, such as module syncs and app publishes
export const useBulkAppOperations = (options: UseBulkAppOperationsOptions): UseBulkAppOperationsReturn => {
    const { appliedTemplateContainer, applicationEnvironmentsContainer } = options;
    const [isProcessing, setIsProcessing] = useState(false);
    const [logs, setLogs] = useState<string[]>([]);

    const addLog = useCallback((messages: string[]) => {
        setLogs(prev => [...prev, ...messages]);
        messages.forEach(msg => console.log(msg));
    }, []);

    const clearLogs = useCallback(() => {
        setLogs([]);
    }, []);

    const syncSingleModule = useCallback(async (
        appId: string,
        moduleId: string,
        syncName: string,
        denom: number | string,
        numerator: number | string
    ): Promise<string | null> => {
        try {
            const moduleResult = await voicifyApi.findAppliedApplicationTemplate(moduleId);
            if (moduleResult.resultType !== "Ok") {
                const error = `Error loading module: ${moduleResult.errors?.join(", ")}`;
                console.error(error);
                return error;
            }

            const formFieldValues = moduleResult.data.templateFormFieldValues;
            const name = moduleResult.data.name;
            const payload = {
                templateFormFieldValues: formFieldValues,
                dynamicFormSections: moduleResult.data.templateForm?.templateFormSections?.filter(s => s?.name) || []
            };

            const queueSyncResult = await voicifyApi.queueUpdateApplicationFromTemplateFormsSync(
                appId,
                moduleId,
                payload
            );
            if (queueSyncResult.resultType !== "Ok") {
                const error = `Error queuing module sync: ${queueSyncResult.errors?.join(", ")}`;
                console.error(error);
                return error;
            }

            const syncId = queueSyncResult.data.id;
            let complete = false;
            const secondWait = 4;
            const secondsLimit = 600;
            const tryLimit = secondsLimit / secondWait;
            let tries = 0;

            while (!complete) {
                if (tries >= tryLimit) {
                    const error = `#${numerator}/${denom} Module stuck: ${name} after ${secondsLimit} seconds`;
                    console.error(error);
                    return error;
                }

                const syncStatusResult = await voicifyApi.getAppliedTemplateSync(syncId);
                if (syncStatusResult.resultType !== "Ok") {
                    const error = `Error loading module sync: ${syncStatusResult.errors?.join(", ")}`;
                    console.error(error);
                    return error;
                }

                const stage = syncStatusResult.data.stage;

                if (stage === "Unknown") {
                    const error = `Error syncing: ${syncStatusResult.data?.error}`;
                    console.error(error);
                    return error;
                } else if (stage === "Queued" || stage === "Processing") {
                    await new Promise(resolve => setTimeout(resolve, secondWait * 1000));
                    tries++;
                    addLog([`#${numerator}/${denom} Waiting for ${syncName} sync, stage: ${stage} (${tries}/${tryLimit})`]);
                } else if (stage === "Complete") {
                    console.log(`${syncName} module sync complete`);
                    complete = true;
                    return null;
                } else {
                    const error = `Error syncing unrecognized status: ${stage}`;
                    console.error(error);
                    return error;
                }
            }

            return null;
        } catch (error: any) {
            const errorMsg = `Exception syncing module ${syncName}: ${error.toString()}`;
            console.error(errorMsg);
            return errorMsg;
        }
    }, [addLog]);

    const syncApps = useCallback(async (
        apps: ApplicationModel[],
        sharedTopicsApp?: ApplicationModel | null,
        concurrencyLimit: number = 8
    ): Promise<{ successCount: number; errorCount: number }> => {
        if (!appliedTemplateContainer) {
            addLog(['Applied template container not available']);
            return { successCount: 0, errorCount: 0 };
        }

        if (apps.length === 0) {
            addLog(['No applications to sync']);
            return { successCount: 0, errorCount: 0 };
        }

        setIsProcessing(true);
        clearLogs();

        let successCount = 0;
        let errorCount = 0;

        try {
            const startDate = new Date();

            // Sync shared topics app first if provided
            if (sharedTopicsApp) {
                addLog([`Starting sync for ${sharedTopicsApp.name}...`]);

                const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(sharedTopicsApp.id);
                if (appliedTemplatesResult?.resultType === "Ok" && appliedTemplatesResult.data.length > 0) {
                    for (const appliedTemplate of appliedTemplatesResult.data) {
                        addLog([`Syncing ${sharedTopicsApp.name} - ${appliedTemplate.name}...`]);

                        const result = await syncSingleModule(
                            sharedTopicsApp.id,
                            appliedTemplate.id,
                            sharedTopicsApp.name,
                            1,
                            1
                        );

                        if (result) {
                            addLog([`✗ ${sharedTopicsApp.name} - ${appliedTemplate.name}: ${result}`]);
                            setIsProcessing(false);
                            return { successCount, errorCount: errorCount + 1 };
                        } else {
                            addLog([`✓ ${sharedTopicsApp.name} - ${appliedTemplate.name}: Complete`]);
                        }
                    }
                }
            }

            // Filter out shared topics app from location apps
            const locationApps = apps.filter(app => app.id !== sharedTopicsApp?.id);

            if (locationApps.length === 0) {
                const endDate = new Date();
                const duration = (endDate.getTime() - startDate.getTime()) / 1000;
                addLog([`\n✓ Module sync completed: ${successCount} successful, ${errorCount} failed`, `Total duration: ${duration} seconds`]);
                setIsProcessing(false);
                return { successCount, errorCount };
            }

            addLog([`\nSyncing ${locationApps.length} location apps...`]);

            let appQueue = [...locationApps];
            let counter = 0;
            const appsCount = appQueue.length;

            const worker = async (workerIdx: number) => {
                while (appQueue.length > 0) {
                    const app = appQueue.shift();
                    if (!app) return;
                    counter++;
                    const subCounter = counter;
                    try {
                        addLog([`#${subCounter}/${appsCount}[Worker ${workerIdx}] Starting ${app.name}...`]);

                        const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(app.id);

                        if (appliedTemplatesResult?.resultType === "Ok" && appliedTemplatesResult.data.length > 0) {
                            for (const appliedTemplate of appliedTemplatesResult.data) {
                                const result = await syncSingleModule(
                                    app.id,
                                    appliedTemplate.id,
                                    app.name,
                                    appsCount,
                                    subCounter
                                );

                                if (result) {
                                    appQueue.length = 0;
                                    throw new Error(result);
                                }
                            }

                            successCount++;
                            addLog([`#${subCounter}/${appsCount} ✓ ${app.name}: Complete`]);
                        }
                    } catch (error: any) {
                        errorCount++;
                        appQueue.length = 0;
                        addLog([`#${subCounter}/${appsCount} ✗ ${app.name}: ${error.toString()}`]);
                        throw error;
                    }
                }
            };

            const workers = [];
            for (let i = 0; i < Math.min(concurrencyLimit, locationApps.length); i++) {
                workers.push(worker(i));
            }

            try {
                await Promise.all(workers);
            } catch (error: any) {
                console.error("One of the syncs failed, stopping all work");
                addLog([`\n✗ Sync stopped due to error: ${error.toString()}`]);
                setIsProcessing(false);
                return { successCount, errorCount };
            }

            const endDate = new Date();
            const duration = (endDate.getTime() - startDate.getTime()) / 1000;

            addLog([
                `\n✓ Module sync completed: ${successCount} successful, ${errorCount} failed`,
                `Total duration: ${duration} seconds`
            ]);
        } catch (error: any) {
            addLog([`Fatal error: ${error.toString()}`]);
        } finally {
            setIsProcessing(false);
        }

        return { successCount, errorCount };
    }, [appliedTemplateContainer, syncSingleModule, addLog, clearLogs]);

    const publishApps = useCallback(async (
        apps: ApplicationModel[],
        concurrencyLimit: number = 7
    ): Promise<{ successCount: number; errorCount: number }> => {
        if (!applicationEnvironmentsContainer) {
            addLog(['Application environments container not available']);
            return { successCount: 0, errorCount: 0 };
        }

        if (apps.length === 0) {
            addLog(['No applications to publish']);
            return { successCount: 0, errorCount: 0 };
        }

        setIsProcessing(true);
        clearLogs();

        let successCount = 0;
        let errorCount = 0;

        try {
            const startDate = new Date();
            const totalCount = apps.length;

            addLog([`Publishing templates for ${totalCount} locations with concurrency limit of ${concurrencyLimit}...`]);

            const publishSingleApp = async (app: ApplicationModel, index: number): Promise<BulkOperationResult> => {
                try {
                    addLog([`#${index}/${totalCount} Starting publish for ${app.name}...`]);

                    const assistantAppResult = await voicifyApi.getAssistantApplication(app.id);
                    if (assistantAppResult.resultType !== "Ok") {
                        const error = `Error loading assistant application: ${assistantAppResult.errors?.join(", ")}`;
                        addLog([`#${index}/${totalCount} ✗ ${app.name}: ${error}`]);
                        return { success: false, appName: app.name, error };
                    }

                    const envId = assistantAppResult.data.environments?.find(e => e.name === "Production")?.id;
                    if (!envId?.length) {
                        const error = "No production environment found";
                        addLog([`#${index}/${totalCount} ✗ ${app.name}: ${error}`]);
                        return { success: false, appName: app.name, error };
                    }

                    const backupResult = await voicifyApi.queueBackupAndPublishApplication(
                        app.id,
                        envId,
                        { name: `Bulk template publish - ${new Date().toISOString()}` }
                    );

                    if (backupResult.resultType !== "Ok") {
                        const error = `Error queuing backup and publish: ${backupResult.errors?.join(", ")}`;
                        addLog([`#${index}/${totalCount} ✗ ${app.name}: ${error}`]);
                        return { success: false, appName: app.name, error };
                    }

                    const publishId = backupResult.data.publish.id;
                    addLog([`#${index}/${totalCount} Publish queued for ${app.name}, waiting for completion...`]);

                    const waitResult = await applicationEnvironmentsContainer.waitForPublish(publishId);

                    if (waitResult?.resultType === "Ok") {
                        addLog([`#${index}/${totalCount} ✓ ${app.name}: Publish completed successfully`]);
                        return { success: true, appName: app.name };
                    } else {
                        const error = waitResult?.errors?.join(", ") || "Publish failed or timed out";
                        addLog([`#${index}/${totalCount} ✗ ${app.name}: ${error}`]);
                        return { success: false, appName: app.name, error };
                    }
                } catch (error: any) {
                    const errorMsg = error.toString();
                    addLog([`#${index}/${totalCount} ✗ ${app.name}: ${errorMsg}`]);
                    return { success: false, appName: app.name, error: errorMsg };
                }
            };

            let appQueue = [...apps];
            let counter = 0;

            const worker = async () => {
                while (appQueue.length > 0) {
                    const app = appQueue.shift();
                    if (!app) break;

                    counter++;
                    const currentIndex = counter;

                    const result = await publishSingleApp(app, currentIndex);

                    if (result.success) {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                }
            };

            const workers = [];
            for (let i = 0; i < Math.min(concurrencyLimit, apps.length); i++) {
                workers.push(worker());
            }

            await Promise.all(workers);

            const endDate = new Date();
            const duration = (endDate.getTime() - startDate.getTime()) / 1000;

            addLog([
                `\n✓ Template publish completed: ${successCount} successful, ${errorCount} failed`,
                `Total duration: ${duration} seconds`
            ]);
        } catch (error: any) {
            addLog([`Fatal error: ${error.toString()}`]);
        } finally {
            setIsProcessing(false);
        }

        return { successCount, errorCount };
    }, [applicationEnvironmentsContainer, addLog, clearLogs]);

    return {
        isProcessing,
        logs,
        syncSingleModule,
        syncApps,
        publishApps,
        clearLogs
    };
};

export default useBulkAppOperations;
